import * as THREE from 'three';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';

// Mirrors createLShapedFloor in useRoomLayout (shape XY maps to world XZ).
export type LShapedFloorLayoutParams = {
    leftWidth: number;
    rightWidth: number;
    leftDepth: number;
    rightDepth: number;
    positionX: number;
    positionZ: number;
    leftDepthInner: number;
    rightWidthInner: number;
    legSplitY: number;
    topY: number;
};

export const getLShapedFloorLayoutParams = (
    dims = getRoomDimensionsComputed()
): LShapedFloorLayoutParams | null => {
    if (dims.roomType !== 'LSHAPED') return null;

    const scale = dims.scale;
    const leftWidth = (dims.roomLeftWidth || 0) * scale - 25;
    const rightWidth = (dims.roomRightWidth || 0) * scale + 50;
    const rightDepth = (dims.roomRightDepth || 0) * scale - 25;
    const leftDepth = (dims.roomLeftDepth || 0) * scale + 60;

    return {
        leftWidth,
        rightWidth,
        leftDepth,
        rightDepth,
        positionX: -(rightWidth / 2) + 60,
        positionZ: leftWidth / 2,
        leftDepthInner: leftDepth - 50,
        rightWidthInner: rightWidth - 50,
        legSplitY: leftWidth - rightDepth,
        topY: leftWidth + 25,
    };
};

export const isLShapedShapePointOnFloor = (
    shapeSpaceX: number,
    shapeSpaceY: number,
    layout: LShapedFloorLayoutParams,
    tolerance = 0
): boolean => {
    const {leftDepthInner, rightWidthInner, legSplitY, topY} = layout;
    const insideThickerLeg =
        shapeSpaceX >= -tolerance &&
        shapeSpaceX <= leftDepthInner + tolerance &&
        shapeSpaceY >= -tolerance &&
        shapeSpaceY <= legSplitY + tolerance;
    const insideUpperArm =
        shapeSpaceX >= -tolerance &&
        shapeSpaceX <= rightWidthInner + tolerance &&
        shapeSpaceY >= legSplitY - tolerance &&
        shapeSpaceY <= topY + tolerance;
    return insideThickerLeg || insideUpperArm;
};

export const worldXZToLShapedShape = (
    worldX: number,
    worldZ: number,
    layout: LShapedFloorLayoutParams,
    out: {sx: number; sy: number}
) => {
    out.sx = worldX - layout.positionX;
    out.sy = layout.positionZ - worldZ;
};

export const isLShapedFootprintFullyInsideFloor = (
    worldBounds: THREE.Box3,
    layout: LShapedFloorLayoutParams,
    footprintTolerance = 0
): boolean => {
    const shapeCoords = {sx: 0, sy: 0};
    const footprintCornersXZ: Array<[number, number]> = [
        [worldBounds.min.x, worldBounds.min.z],
        [worldBounds.max.x, worldBounds.min.z],
        [worldBounds.max.x, worldBounds.max.z],
        [worldBounds.min.x, worldBounds.max.z],
    ];

    for (const [worldX, worldZ] of footprintCornersXZ) {
        worldXZToLShapedShape(worldX, worldZ, layout, shapeCoords);
        if (
            !isLShapedShapePointOnFloor(
                shapeCoords.sx,
                shapeCoords.sy,
                layout,
                footprintTolerance
            )
        ) {
            return false;
        }
    }

    return true;
};

// Clamp bounds are one rectangle, so in an L-room they span the cut-out too.
// Rectangular rooms and unresolvable layouts fall back to those bounds.
export const isFootprintOnWalkableFloor = (
    worldBounds: THREE.Box3,
    tolerance = 0
): boolean => {
    const dims = getRoomDimensionsComputed();
    if (dims.roomType === 'RECTANGULAR') {
        // Matches createRectangularFloor. roomWidth is already scaled by
        // getRoomDimensionsComputed; roomDepth remains in source units.
        const floorWidth = dims.roomWidth || 0;
        const floorDepth = (dims.roomDepth || 0) * dims.scale;
        const minX = -floorWidth / 2 + 35;
        const maxX = floorWidth / 2 + 35;
        const minZ = -floorDepth / 2 - 12.5;
        const maxZ = floorDepth / 2 - 12.5;

        return (
            worldBounds.min.x >= minX - tolerance &&
            worldBounds.max.x <= maxX + tolerance &&
            worldBounds.min.z >= minZ - tolerance &&
            worldBounds.max.z <= maxZ + tolerance
        );
    }

    const layout = getLShapedFloorLayoutParams(dims);
    if (!layout) return true;

    return isLShapedFootprintFullyInsideFloor(worldBounds, layout, tolerance);
};
